/**
 * API 错误实时通知组件
 * 监听 request.ts 派发的 api-error 事件，在页面上显示 Snackbar
 * 只展示用户可读的错误消息，不展示接口路径、状态码、历史记录等调试信息
 * 倒计时消息（3秒后跳转）常显不自动关闭；普通错误 4 秒后自动消失
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Snackbar, Alert, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ApiErrorDetail {
  url: string;
  method: string;
  status: number | undefined;
  code?: number;
  message: string;
  timestamp: number;
}

export function ApiErrorNotifier() {
  const [queue, setQueue] = useState<ApiErrorDetail[]>([]);
  const [open, setOpen] = useState(false);
  // 追踪最新消息，用于持续更新而非关闭再打开
  const [currentMsg, setCurrentMsg] = useState<string>('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ApiErrorDetail>).detail;
      setQueue((prev) => [...prev, detail]);
    };
    window.addEventListener('api-error', handler);
    return () => {
      window.removeEventListener('api-error', handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  /** 处理当前消息 */
  useEffect(() => {
    if (queue.length === 0) {
      setOpen(false);
      return;
    }
    const latest = queue[queue.length - 1];
    setCurrentMsg(latest.message);
    setOpen(true);

    // 如果消息包含"将跳转"（倒计时类），不自动关闭
    if (latest.message.includes('将跳转')) {
      // 不设自动关闭
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    // 普通错误 5 秒后自动关闭
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setOpen(false);
      setQueue([]);
    }, 5000);
  }, [queue]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setQueue([]);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return (
    <Snackbar
      open={open}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{ maxWidth: 480 }}
    >
      <Alert
        severity="error"
        variant="filled"
        sx={{ width: '100%', alignItems: 'center' }}
        action={
          <IconButton size="small" color="inherit" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      >
        {currentMsg}
      </Alert>
    </Snackbar>
  );
}
